<script setup lang="ts">
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { ElMessage, ElMessageBox } from 'element-plus';
import { useAuthStore } from '@/stores/auth.store';
import { useSessionStore } from '@/stores/session.store';
import { useInboxStore } from '@/stores/inbox.store';
import { extractApiError } from '@/api/client';
import { useAsyncLoad } from '@/composables/useAsyncLoad';
import { useIsMobile } from '@/composables/useIsMobile';
import * as inboxApi from '@/api/inbox';
import * as encountersApi from '@/api/encounters';
import type { InboxMessageModel, InboxMessageType } from '@/types/inbox';
import type { EncounterOutcome } from '@/types/encounters';

const auth = useAuthStore();
const session = useSessionStore();
const inboxStore = useInboxStore();
const router = useRouter();
const isMobile = useIsMobile();

// Id du player-universe ACTIF (≠ auth.user.id qui est l'id du COMPTE). Les
// endpoints inbox attendent un playerUniverseId : on lit la session.
const activePuId = computed(() => session.activePlayerUniverseId);

function isBattleReportLink(m: InboxMessageModel): boolean {
  return m.messageType === 'BattleReport'
    && m.relatedEntityType === 'Battle'
    && !!m.relatedEntityId;
}

function openBattleReport(m: InboxMessageModel): void {
  if (m.relatedEntityId) {
    router.push({ name: 'battle', params: { id: m.relatedEntityId } });
  }
}

/** Wiki §33.7 — un message Inbox d'encounter ouvert pointe sur CreatureEncounter. */
function isEncounterLink(m: InboxMessageModel): boolean {
  return m.relatedEntityType === 'CreatureEncounter' && !!m.relatedEntityId;
}

/** P1.5-bis -- un message Trade de resolution marche pointe sur MarketListing.
 *  Le CTA renvoie sur /market ou le joueur peut voir l etat final du listing. */
function isMarketLink(m: InboxMessageModel): boolean {
  return m.relatedEntityType === 'MarketListing' && !!m.relatedEntityId;
}

function openMarketListing(): void {
  // Pour V1 on renvoie sur /market generique : la MarketView affiche les
  // listings filtres par activeOnly = true par defaut, le joueur peut
  // decocher pour voir le sien resolu. Un deep-link /market/{id} serait
  // mieux mais necessite une vue dediee qu on n a pas encore.
  router.push({ name: 'market' });
}

/** AllianceInvitation : MessageType='AllianceInvitation', RelatedEntityType='Alliance',
 *  RelatedEntityId = alliance.id. Le CTA accepter/decliner appelle
 *  /me/alliance/accept ou /decline. Le service backend consomme l'invite
 *  (marque le message lu) en meme temps. */
function isAllianceInviteLink(m: InboxMessageModel): boolean {
  return m.messageType === 'AllianceInvitation'
    && m.relatedEntityType === 'Alliance'
    && !!m.relatedEntityId
    && !m.readAt;  // une fois consomme, on cache les boutons
}

/** TreatyProposal : MessageType='TreatyProposal', RelatedEntityType='Alliance',
 *  Body = JSON {fromAllianceId, fromTag, fromName, kind, expiresAt, casusBelli}.
 *  Le CTA accepte/decline via inboxMessageId (le backend parse le JSON). */
function isTreatyProposalLink(m: InboxMessageModel): boolean {
  return m.messageType === 'TreatyProposal' && !m.readAt;
}

interface TreatyProposalParsed {
  fromTag?: string;
  fromName?: string;
  kind?: string;
}
function parseTreatyProposal(m: InboxMessageModel): TreatyProposalParsed {
  try {
    const obj = JSON.parse(m.body);
    return {
      fromTag: typeof obj.fromTag === 'string' ? obj.fromTag : undefined,
      fromName: typeof obj.fromName === 'string' ? obj.fromName : undefined,
      kind: typeof obj.kind === 'string' ? obj.kind : undefined,
    };
  } catch { return {}; }
}

const allianceActionPending = ref(false);
async function acceptAllianceInvitation(m: InboxMessageModel): Promise<void> {
  if (!auth.user || !m.relatedEntityId) return;
  allianceActionPending.value = true;
  try {
    const { acceptAllianceInvite } = await import('@/api/alliance');
    await acceptAllianceInvite({
      playerUniverseId: activePuId.value ?? 0,
      allianceId: m.relatedEntityId,
    });
    ElMessage.success('Vous avez rejoint l\'alliance.');
    void inboxStore.refresh();
    await load();
    router.push({ name: 'alliance' });
  } catch (err) {
    ElMessage.error(extractApiError(err));
  } finally {
    allianceActionPending.value = false;
  }
}
async function declineAllianceInvitation(m: InboxMessageModel): Promise<void> {
  if (!auth.user || !m.relatedEntityId) return;
  allianceActionPending.value = true;
  try {
    const { declineAllianceInvite } = await import('@/api/alliance');
    await declineAllianceInvite({
      playerUniverseId: activePuId.value ?? 0,
      allianceId: m.relatedEntityId,
    });
    ElMessage.success('Invitation declinee.');
    void inboxStore.refresh();
    await load();
  } catch (err) {
    ElMessage.error(extractApiError(err));
  } finally {
    allianceActionPending.value = false;
  }
}

/** AllianceJoinRequest : un joueur demande a rejoindre NOTRE alliance. Le
 *  message est adresse aux Founders/Officers ; le CTA accepte/decline via
 *  l'InboxMessageId. fromPlayerUniverseId = le demandeur. */
function isAllianceJoinRequestLink(m: InboxMessageModel): boolean {
  return m.messageType === 'AllianceJoinRequest' && !m.readAt;
}
async function acceptJoinRequestAction(m: InboxMessageModel): Promise<void> {
  if (!auth.user) return;
  allianceActionPending.value = true;
  try {
    const { acceptJoinRequest } = await import('@/api/alliance');
    await acceptJoinRequest({
      callerPlayerUniverseId: activePuId.value ?? 0,
      inboxMessageId: m.id,
    });
    ElMessage.success('Demande acceptée — le joueur a rejoint l\'alliance.');
    void inboxStore.refresh();
    await load();
  } catch (err) {
    ElMessage.error(extractApiError(err));
  } finally {
    allianceActionPending.value = false;
  }
}
async function declineJoinRequestAction(m: InboxMessageModel): Promise<void> {
  if (!auth.user) return;
  allianceActionPending.value = true;
  try {
    const { declineJoinRequest } = await import('@/api/alliance');
    await declineJoinRequest({
      callerPlayerUniverseId: activePuId.value ?? 0,
      inboxMessageId: m.id,
    });
    ElMessage.success('Demande déclinée.');
    void inboxStore.refresh();
    await load();
  } catch (err) {
    ElMessage.error(extractApiError(err));
  } finally {
    allianceActionPending.value = false;
  }
}

async function acceptTreatyProposalAction(m: InboxMessageModel): Promise<void> {
  if (!auth.user) return;
  allianceActionPending.value = true;
  try {
    const { acceptTreatyProposal } = await import('@/api/alliance');
    await acceptTreatyProposal({
      callerPlayerUniverseId: activePuId.value ?? 0,
      inboxMessageId: m.id,
    });
    ElMessage.success('Traite signe.');
    void inboxStore.refresh();
    await load();
    router.push({ name: 'alliance' });
  } catch (err) {
    ElMessage.error(extractApiError(err));
  } finally {
    allianceActionPending.value = false;
  }
}
async function declineTreatyProposalAction(m: InboxMessageModel): Promise<void> {
  if (!auth.user) return;
  allianceActionPending.value = true;
  try {
    const { declineTreatyProposal } = await import('@/api/alliance');
    await declineTreatyProposal({
      callerPlayerUniverseId: activePuId.value ?? 0,
      inboxMessageId: m.id,
    });
    ElMessage.success('Proposition declinee.');
    void inboxStore.refresh();
    await load();
  } catch (err) {
    ElMessage.error(extractApiError(err));
  } finally {
    allianceActionPending.value = false;
  }
}

const encounterDialog = ref(false);
const encounterTarget = ref<InboxMessageModel | null>(null);
const encounterSubmitting = ref(false);

function openEncounterDialog(m: InboxMessageModel): void {
  encounterTarget.value = m;
  encounterDialog.value = true;
}

async function resolveEncounterAs(outcome: EncounterOutcome): Promise<void> {
  if (!encounterTarget.value?.relatedEntityId) return;
  encounterSubmitting.value = true;
  try {
    const r = await encountersApi.resolveEncounter(
      encounterTarget.value.relatedEntityId,
      outcome,
    );
    const lootCount = Object.keys(r.loot).length;
    let msg = `Issue ${outcome} appliquee.`;
    if (r.battleId) {
      msg += ` Battle #${r.battleId} ${r.attackerWon ? 'gagne' : 'perdu'}.`;
    }
    if (r.negociationSuccess === false) {
      msg += ' Negociation echouee.';
    }
    if (lootCount > 0) {
      msg += ` Loot : ${lootCount} ressource(s).`;
    }
    ElMessage.success(msg);
    encounterDialog.value = false;
    encounterTarget.value = null;
    await load();
  } catch (err) {
    ElMessage.error(extractApiError(err));
  } finally {
    encounterSubmitting.value = false;
  }
}

const messages = ref<InboxMessageModel[]>([]);
const total = ref(0);
const selected = ref<InboxMessageModel | null>(null);

// =====================================================================
// Mobile (<768px) : navigation mono-volet (liste <-> detail) + swipe-to-delete
// sur les lignes (style messagerie). Sur desktop, les handlers tactiles ne se
// declenchent pas et le layout 2 colonnes est conserve.
// =====================================================================
const SWIPE_REVEAL = 84; // largeur (px) du bouton "Supprimer" revele au swipe
const swipeOpenId = ref<number | null>(null); // ligne actuellement ouverte
const dragId = ref<number | null>(null);       // ligne en cours de drag
const dragDx = ref(0);                          // offset horizontal courant
let dragStartX = 0;
let dragStartY = 0;
let dragAxis: 'h' | 'v' | null = null;          // verrou d'axe (h=swipe, v=scroll)

function itemOffset(m: InboxMessageModel): number {
  if (dragId.value === m.id) return dragDx.value;
  return swipeOpenId.value === m.id ? -SWIPE_REVEAL : 0;
}
function onTouchStart(m: InboxMessageModel, e: TouchEvent): void {
  dragStartX = e.touches[0].clientX;
  dragStartY = e.touches[0].clientY;
  dragAxis = null;
  dragId.value = m.id;
  dragDx.value = swipeOpenId.value === m.id ? -SWIPE_REVEAL : 0;
}
function onTouchMove(m: InboxMessageModel, e: TouchEvent): void {
  if (dragId.value !== m.id) return;
  const dx = e.touches[0].clientX - dragStartX;
  const dy = e.touches[0].clientY - dragStartY;
  if (dragAxis === null && (Math.abs(dx) > 8 || Math.abs(dy) > 8)) {
    dragAxis = Math.abs(dx) > Math.abs(dy) ? 'h' : 'v';
  }
  if (dragAxis !== 'h') return; // laisse le scroll vertical de la liste
  e.preventDefault(); // empeche le scroll pendant un swipe horizontal
  const base = swipeOpenId.value === m.id ? -SWIPE_REVEAL : 0;
  dragDx.value = Math.max(-SWIPE_REVEAL, Math.min(0, base + dx));
}
function onTouchEnd(m: InboxMessageModel): void {
  if (dragId.value !== m.id) return;
  if (dragAxis === 'h') {
    swipeOpenId.value = dragDx.value < -SWIPE_REVEAL / 2 ? m.id : null;
  }
  dragId.value = null;
  dragDx.value = 0;
}
function onItemClick(m: InboxMessageModel): void {
  // Une ligne ouverte (swipe) se referme au tap au lieu d'ouvrir le message.
  if (swipeOpenId.value !== null) { swipeOpenId.value = null; return; }
  void openMessage(m);
}
function backToList(): void { selected.value = null; }
// Loading + erreurs du fetch initial via useAsyncLoad (les actions
// d'ouverture/suppression d'un message gardent leur try/catch local car
// elles veulent un toast d'action specifique, pas le prefix "Inbox :").
const { loading, run } = useAsyncLoad({ errorPrefix: 'Inbox :' });

const filters = reactive({
  messageType: undefined as InboxMessageType | undefined,
  unreadOnly: false,
});

const composeDialog = ref(false);
const composeForm = reactive({
  toPlayerUniverseId: 0,
  subject: '',
  body: '',
});

async function load(): Promise<void> {
  if (activePuId.value == null) return;
  await run(async () => {
    const r = await inboxApi.fetchInbox({
      ownerPlayerUniverseId: activePuId.value!,
      messageType: filters.messageType,
      unreadOnly: filters.unreadOnly || undefined,
    });
    // Garde defensive : si l'API renvoie une forme inattendue (sans items),
    // on retombe sur [] plutot que de laisser un undefined planter le
    // computed unreadCount (.filter sur undefined).
    messages.value = r.items ?? [];
    total.value = r.total ?? 0;
  });
}

onMounted(load);
watch(filters, load, { deep: true });

async function openMessage(msg: InboxMessageModel): Promise<void> {
  selected.value = msg;
  if (!msg.readAt) {
    try {
      await inboxApi.markAsRead(msg.id);
      msg.readAt = new Date().toISOString();
      // Refresh du badge global pour reflet immediat (sinon le poll 60s
      // laisse le compteur faux pendant un long moment).
      void inboxStore.refresh();
    } catch (err) {
      ElMessage.error(extractApiError(err));
    }
  }
}

async function onDelete(msg: InboxMessageModel): Promise<void> {
  try {
    await ElMessageBox.confirm(`Supprimer "${msg.subject}" ?`, 'Confirmation', {
      type: 'warning',
      confirmButtonText: 'Supprimer',
      cancelButtonText: 'Annuler',
    });
  } catch {
    return;
  }
  try {
    await inboxApi.deleteMessage(msg.id);
    ElMessage.success('Message supprime.');
    if (selected.value?.id === msg.id) selected.value = null;
    await load();
  } catch (err) {
    ElMessage.error(extractApiError(err));
  }
}

async function onSend(): Promise<void> {
  if (!auth.user) return;
  if (!composeForm.toPlayerUniverseId || !composeForm.subject.trim()) {
    ElMessage.warning('Destinataire + sujet requis.');
    return;
  }
  try {
    await inboxApi.sendMessage({
      fromPlayerUniverseId: activePuId.value ?? 0,
      toPlayerUniverseId: composeForm.toPlayerUniverseId,
      subject: composeForm.subject,
      body: composeForm.body,
    });
    ElMessage.success('Message envoye.');
    composeDialog.value = false;
    composeForm.toPlayerUniverseId = 0;
    composeForm.subject = '';
    composeForm.body = '';
  } catch (err) {
    ElMessage.error(extractApiError(err));
  }
}

function typeTone(t: InboxMessageType): 'success' | 'warning' | 'danger' | 'info' | '' {
  if (t === 'BattleReport') return 'danger';
  if (t === 'PrivateMessage') return 'info';
  if (t === 'System') return '';
  if (t === 'News') return 'success';
  if (t === 'Trade') return 'warning';
  if (t === 'Espionage') return 'danger';
  return '';
}

const unreadCount = computed(() => (messages.value ?? []).filter((m) => !m.readAt).length);

/** Marque TOUS les non-lus de la boite comme lus (1 appel backend). */
async function markAll(): Promise<void> {
  if (activePuId.value == null || unreadCount.value === 0) return;
  try {
    const n = await inboxApi.markAllAsRead(activePuId.value);
    // Reflet immédiat (sinon attendre le prochain load) + refresh du badge global.
    (messages.value ?? []).forEach((m) => { if (!m.readAt) m.readAt = new Date().toISOString(); });
    void inboxStore.refresh();
    ElMessage.success(`${n} message(s) marqué(s) comme lu(s).`);
  } catch (err) {
    ElMessage.error(extractApiError(err));
  }
}

// Libellés des types (alignés sur les el-option du filtre) pour les boutons/messages.
const TYPE_LABELS: Record<string, string> = {
  BattleReport: 'Rapports de combat',
  PrivateMessage: 'MP',
  System: 'Système',
  News: 'News',
  Trade: 'Échanges',
  Espionage: 'Espionnage',
  Quest: 'Quêtes',
};
/** Libellé du bouton supprimer : tient compte du filtre de type actif. */
const deleteAllLabel = computed(() =>
  filters.messageType ? `Supprimer « ${TYPE_LABELS[filters.messageType] ?? filters.messageType} »` : 'Tout supprimer',
);

/**
 * Supprime les messages de la boite (confirmation obligatoire, destructif).
 * Respecte le FILTRE DE TYPE actif : si un type est sélectionné, ne supprime que
 * ce type ; sinon toute la boite.
 */
async function deleteAllMessages(): Promise<void> {
  if (activePuId.value == null || total.value === 0) return;
  const type = filters.messageType || undefined;
  const scope = type ? `les ${total.value} message(s) « ${TYPE_LABELS[type] ?? type} »` : `les ${total.value} message(s) de ta boîte`;
  try {
    await ElMessageBox.confirm(
      `Supprimer définitivement ${scope} ?`,
      deleteAllLabel.value,
      { type: 'warning', confirmButtonText: 'Supprimer', cancelButtonText: 'Annuler' },
    );
  } catch {
    return; // annulé
  }
  try {
    const n = await inboxApi.deleteAll(activePuId.value, type);
    selected.value = null;
    await load();
    void inboxStore.refresh();
    ElMessage.success(`${n} message(s) supprimé(s).`);
  } catch (err) {
    ElMessage.error(extractApiError(err));
  }
}
</script>

<template>
  <div v-loading="loading" class="inbox-view">
    <header class="inbox-header">
      <div>
        <h1 class="inbox-title">Messages</h1>
        <p class="text-muted">{{ total }} message(s) · {{ unreadCount }} non lu(s)</p>
      </div>
      <div class="inbox-header-actions">
        <el-button :icon="'Select'" :disabled="unreadCount === 0" @click="markAll">
          Tout marquer lu
        </el-button>
        <el-button type="danger" plain :icon="'Delete'" :disabled="total === 0" @click="deleteAllMessages">
          {{ deleteAllLabel }}
        </el-button>
        <el-button type="primary" :icon="'EditPen'" @click="composeDialog = true">
          Nouveau message
        </el-button>
      </div>
    </header>

    <div class="inbox-filters">
      <el-select v-model="filters.messageType" clearable placeholder="Tous types" style="width: 200px">
        <el-option label="Rapports de combat" value="BattleReport" />
        <el-option label="MP" value="PrivateMessage" />
        <el-option label="Systeme" value="System" />
        <el-option label="News" value="News" />
        <el-option label="Echanges" value="Trade" />
        <el-option label="Espionnage" value="Espionage" />
        <el-option label="Quetes" value="Quest" />
      </el-select>
      <el-switch v-model="filters.unreadOnly" active-text="Non lus uniquement" />
    </div>

    <div class="inbox-layout" :class="{ 'inbox-layout--mobile': isMobile }">
      <ul v-show="!isMobile || !selected" class="inbox-list">
        <li
          v-for="m in messages"
          :key="m.id"
          class="inbox-row"
        >
          <!-- Action revelee par le swipe (mobile) : suppression. -->
          <button class="inbox-row-del" type="button" @click.stop="onDelete(m)">
            <span class="inbox-row-del-ico">🗑</span>
            <span class="inbox-row-del-lbl">Suppr.</span>
          </button>
          <div
            class="inbox-item"
            :class="{ 'is-selected': selected?.id === m.id, 'is-unread': !m.readAt }"
            :style="{
              transform: `translateX(${itemOffset(m)}px)`,
              transition: dragId === m.id ? 'none' : 'transform 0.2s ease',
            }"
            @click="onItemClick(m)"
            @touchstart.passive="onTouchStart(m, $event)"
            @touchmove="onTouchMove(m, $event)"
            @touchend="onTouchEnd(m)"
            @touchcancel="onTouchEnd(m)"
          >
            <div class="inbox-item-meta">
              <el-tag :type="typeTone(m.messageType)" size="small" effect="dark">
                {{ m.messageType }}
              </el-tag>
              <span class="inbox-item-from" v-if="m.fromPlayerUniverseId">
                de #{{ m.fromPlayerUniverseId }}
              </span>
            </div>
            <div class="inbox-item-subject">{{ m.subject }}</div>
          </div>
        </li>
        <li v-if="!messages.length && !loading" class="inbox-empty">
          <el-empty description="Aucun message" :image-size="80" />
        </li>
      </ul>

      <article v-show="!isMobile || selected" class="inbox-detail">
        <template v-if="selected">
          <header class="inbox-detail-header">
            <el-button
              v-if="isMobile"
              class="inbox-back"
              size="small"
              text
              @click="backToList"
            >
              ← Retour
            </el-button>
            <h2>{{ selected.subject }}</h2>
            <el-button
              size="small"
              type="danger"
              :icon="'Delete'"
              text
              @click="onDelete(selected)"
            >
              Supprimer
            </el-button>
          </header>
          <div class="inbox-detail-meta">
            <el-tag :type="typeTone(selected.messageType)" size="small" effect="dark">
              {{ selected.messageType }}
            </el-tag>
            <span v-if="selected.fromPlayerUniverseId">
              de #{{ selected.fromPlayerUniverseId }}
            </span>
          </div>
          <div v-if="isBattleReportLink(selected)" class="inbox-detail-cta">
            <el-button
              type="primary"
              :icon="'Lightning'"
              @click="openBattleReport(selected)"
            >
              Ouvrir le rapport de combat #{{ selected.relatedEntityId }}
            </el-button>
          </div>
          <div v-if="isEncounterLink(selected)" class="inbox-detail-cta">
            <el-button
              type="warning"
              :icon="'Aim'"
              @click="openEncounterDialog(selected)"
            >
              Resoudre la rencontre #{{ selected.relatedEntityId }}
            </el-button>
          </div>
          <div v-if="isMarketLink(selected)" class="inbox-detail-cta">
            <el-button type="success" :icon="'Goods'" @click="openMarketListing">
              Voir au marche
            </el-button>
          </div>
          <div v-if="isAllianceInviteLink(selected)" class="inbox-detail-cta">
            <el-button
              type="success"
              :loading="allianceActionPending"
              @click="acceptAllianceInvitation(selected)"
            >
              Accepter l'invitation
            </el-button>
            <el-button
              type="info" plain
              :loading="allianceActionPending"
              @click="declineAllianceInvitation(selected)"
            >
              Decliner
            </el-button>
          </div>
          <div v-if="isTreatyProposalLink(selected)" class="inbox-detail-cta">
            <el-button
              type="success"
              :loading="allianceActionPending"
              @click="acceptTreatyProposalAction(selected)"
            >
              Signer le traite
              <template v-if="parseTreatyProposal(selected).kind">
                ({{ parseTreatyProposal(selected).kind }})
              </template>
            </el-button>
            <el-button
              type="info" plain
              :loading="allianceActionPending"
              @click="declineTreatyProposalAction(selected)"
            >
              Refuser
            </el-button>
          </div>
          <div v-if="isAllianceJoinRequestLink(selected)" class="inbox-detail-cta">
            <el-button
              type="success"
              :loading="allianceActionPending"
              @click="acceptJoinRequestAction(selected)"
            >
              Accepter la demande
            </el-button>
            <el-button
              type="info" plain
              :loading="allianceActionPending"
              @click="declineJoinRequestAction(selected)"
            >
              Décliner
            </el-button>
          </div>
          <div class="inbox-detail-body"><MessageBody :text="selected.body" /></div>
        </template>
        <el-empty v-else description="Selectionnez un message" :image-size="100" />
      </article>
    </div>

    <el-dialog
      v-model="encounterDialog"
      title="Resoudre la rencontre"
      width="min(92vw, 480px)"
    >
      <p v-if="encounterTarget">
        Choisissez comment votre equipe doit reagir face a la creature
        decrite dans
        <strong>"{{ encounterTarget.subject }}"</strong>.
        Le backend refusera les options invalides (ex. Negociation contre
        une creature hostile de haut danger).
      </p>
      <div class="encounter-outcome-buttons">
        <el-button
          type="danger"
          :loading="encounterSubmitting"
          @click="resolveEncounterAs('Combat')"
        >
          Combat
        </el-button>
        <el-button
          :loading="encounterSubmitting"
          @click="resolveEncounterAs('Fuite')"
        >
          Fuite
        </el-button>
        <el-button
          type="success"
          :loading="encounterSubmitting"
          @click="resolveEncounterAs('Negociation')"
        >
          Negociation
        </el-button>
      </div>
      <template #footer>
        <DialogActions @cancel="encounterDialog = false" />
      </template>
    </el-dialog>

    <el-dialog v-model="composeDialog" title="Nouveau message" width="min(92vw, 540px)">
      <el-form :model="composeForm" label-position="top">
        <el-form-item label="Destinataire (player universe id)">
          <el-input-number v-model="composeForm.toPlayerUniverseId" :min="1" />
        </el-form-item>
        <el-form-item label="Sujet">
          <el-input v-model="composeForm.subject" />
        </el-form-item>
        <el-form-item label="Message">
          <el-input v-model="composeForm.body" type="textarea" :rows="6" />
        </el-form-item>
      </el-form>
      <template #footer>
        <DialogActions @cancel="composeDialog = false">
          <el-button type="primary" @click="onSend">Envoyer</el-button>
        </DialogActions>
      </template>
    </el-dialog>
  </div>
</template>

<style lang="scss" scoped>
.inbox-view {
  max-width: $layout-max-width;
}

.inbox-header {
  @include flex-between;
  margin-bottom: $space-4;
}
.inbox-header-actions {
  display: flex;
  align-items: center;
  gap: $space-2;
  flex-shrink: 0;
}

.inbox-title {
  font-family: $font-display;
  font-size: $font-size-3xl;
  margin: 0 0 $space-1;
}

.inbox-filters {
  display: flex;
  gap: $space-3;
  align-items: center;
  margin-bottom: $space-4;
}

.inbox-layout {
  display: grid;
  grid-template-columns: 320px 1fr;
  gap: $space-4;
  min-height: 500px;
}

.inbox-list {
  @include card-dark;
  @include scrollbar-thin;
  list-style: none;
  margin: 0;
  padding: 0;
  max-height: calc(100vh - 280px);
  overflow-y: auto;
}

// Ligne = conteneur positionne : le bouton "Supprimer" est dessous, l'item
// (opaque) glisse par-dessus au swipe pour le reveler.
.inbox-row {
  position: relative;
  overflow: hidden;
  border-bottom: 1px solid $border-subtle;
}

.inbox-row-del {
  position: absolute;
  inset: 0 0 0 auto;
  width: 84px;
  border: none;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  background: $color-danger;
  color: #fff;
  cursor: pointer;
  font-family: $font-mono;
  .inbox-row-del-ico { font-size: 18px; line-height: 1; }
  .inbox-row-del-lbl { font-size: 10px; text-transform: uppercase; letter-spacing: 0.05em; }
}

.inbox-item {
  position: relative;
  background: $bg-secondary; // opaque : masque le bouton supprimer au repos
  padding: $space-3 $space-4;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: $space-1;

  &:hover {
    background: $bg-tertiary;
  }

  &.is-selected {
    background: rgba($accent-primary, 0.15);
  }

  &.is-unread .inbox-item-subject {
    font-weight: $font-weight-semibold;
    color: $color-neon-cyan;
  }
}

.inbox-item-meta {
  display: flex;
  align-items: center;
  gap: $space-2;
  font-size: $font-size-xs;
  color: $text-muted;
}

.inbox-item-subject {
  font-size: $font-size-sm;
  color: $text-secondary;
  @include truncate(2);
}

.inbox-empty {
  padding: $space-6;
}

.inbox-detail {
  @include card-dark;
  padding: $space-5;
  display: flex;
  flex-direction: column;
  gap: $space-4;
  min-height: 400px;
  min-width: 0; // autorise le retrecissement dans la grille (anti-debordement)
}

.inbox-detail-header {
  @include flex-between;
  flex-wrap: wrap;
  gap: $space-2;

  h2 {
    font-family: $font-display;
    font-size: $font-size-xl;
    margin: 0;
    min-width: 0;
    overflow-wrap: anywhere;
  }
}
.inbox-back { flex: 0 0 auto; }

.inbox-detail-meta {
  display: flex;
  align-items: center;
  gap: $space-3;
  font-size: $font-size-sm;
  color: $text-muted;
}

.inbox-detail-body {
  font-family: $font-body;
  white-space: pre-wrap;
  overflow-wrap: anywhere; // coupe les mots/URLs trop longs (anti-debordement)
  word-break: break-word;
  min-width: 0;
  margin: 0;
  color: $text-primary;
  line-height: $line-height-relaxed;
}

.inbox-detail-cta {
  display: flex;
  flex-wrap: wrap;
  gap: $space-2;
}

.encounter-outcome-buttons {
  display: flex;
  gap: $space-3;
  justify-content: center;
  margin-top: $space-4;
}

// ---------------------------------------------------------------------------
// Mobile (<768px) : navigation mono-volet. La liste OU le detail occupe toute
// la largeur (bascule via v-show selon `selected`). Header/filtres empiles.
// ---------------------------------------------------------------------------
@media (max-width: 767.99px) {
  .inbox-header {
    flex-direction: column;
    align-items: stretch;
    gap: $space-3;
  }
  .inbox-header-actions {
    // Empile les boutons en pleine largeur, alignes. margin-left:0 neutralise la
    // marge EP entre boutons adjacents (.el-button + .el-button) qui decalait les
    // suivants quand ils passent a la ligne.
    flex-direction: column;
    align-items: stretch;
    gap: $space-2;
    :deep(.el-button) {
      width: 100%;
      margin-left: 0;
    }
  }
  .inbox-filters {
    flex-wrap: wrap;
    .el-select { width: 100% !important; }
  }
  .inbox-layout {
    grid-template-columns: 1fr; // une seule colonne : la vue active prend tout
    min-height: 0;
  }
  .inbox-list {
    max-height: none; // un seul volet visible -> pas de double scroll
  }
  .inbox-detail {
    min-height: 0;
    padding: $space-4;
  }
}

// Le swipe-to-delete est tactile/mobile uniquement : on masque le bouton
// revele sur desktop (pas de geste, evite un focusable cache dans le tab order).
@media (min-width: 768px) {
  .inbox-row-del { display: none; }
}
</style>
